import React from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import { Droplet, TrendingUp, AlertTriangle, CloudRain } from 'lucide-react';
import { useMode } from '@/contexts/ModeContext';
import useTranslation from '@/hooks/use-translation';
import { cn } from '@/lib/utils';

interface PredictionData {
  date: string;
  actual?: number;
  predicted: number;
  baseline: number;
}

interface WaterPredictionProps {
  className?: string;
}

export function WaterPrediction({ className }: WaterPredictionProps) {
  const { mode } = useMode();
  const { translate } = useTranslation();

  // Sample prediction data - replace with actual API data
  const predictionData: PredictionData[] = [
    { date: 'Mon', actual: 100, predicted: 110, baseline: 105 },
    { date: 'Tue', actual: 120, predicted: 115, baseline: 105 },
    { date: 'Wed', actual: 90, predicted: 95, baseline: 105 },
    { date: 'Thu', actual: 105, predicted: 100, baseline: 105 },
    { date: 'Fri', actual: 95, predicted: 98, baseline: 105 },
    { date: 'Sat', predicted: 92, baseline: 105 },
    { date: 'Sun', predicted: 88, baseline: 105 },
  ];

  const insights = [
    {
      icon: <TrendingUp className="h-5 w-5 text-nature-500" />,
      text: translate(
        mode === 'household'
          ? "Your water usage is predicted to decrease by 12% next week"
          : "Irrigation efficiency is predicted to improve by 15% next week"
      ),
    },
    {
      icon: <AlertTriangle className="h-5 w-5 text-amber-500" />,
      text: translate(
        mode === 'household'
          ? "Potential leak detected based on unusual night usage patterns"
          : "Field sector B shows signs of over-irrigation"
      ),
    },
    {
      icon: <CloudRain className="h-5 w-5 text-blue-500" />,
      text: translate(
        mode === 'household'
          ? "Expected rainfall may reduce outdoor water needs"
          : "Adjust irrigation schedule based on forecasted precipitation"
      ),
    },
  ];

  return (
    <Card className={cn("", className)}>
      <CardHeader>
        <CardTitle>{translate("Water Usage Predictions")}</CardTitle>
        <CardDescription>
          {translate(
            mode === 'household'
              ? "7-day forecast of your household water consumption"
              : "7-day irrigation requirement forecast"
          )}
        </CardDescription>
      </CardHeader>
      <CardContent>
        <div className="space-y-8">
          <div className="h-[300px]">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={predictionData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="date" />
                <YAxis />
                <Tooltip />
                <Legend />
                <Line
                  type="monotone"
                  dataKey="actual"
                  name={translate("Actual Usage")}
                  stroke="#0ea5e9"
                  strokeWidth={2}
                  dot={{ r: 4 }}
                />
                <Line
                  type="monotone"
                  dataKey="predicted"
                  name={translate("Predicted")}
                  stroke="#22c55e"
                  strokeWidth={2}
                  dot={{ r: 4 }}
                />
                <Line
                  type="monotone"
                  dataKey="baseline"
                  name={translate("Baseline")}
                  stroke="#94a3b8"
                  strokeDasharray="4 4"
                />
              </LineChart>
            </ResponsiveContainer>
          </div>

          <div className="space-y-4">
            {insights.map((insight, index) => (
              <div
                key={index}
                className="flex items-start gap-3 p-3 rounded-lg bg-muted/50"
              >
                {insight.icon}
                <p className="text-sm">{insight.text}</p>
              </div>
            ))}
          </div>
        </div>
      </CardContent>
    </Card>
  );
} 